import { useState, useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import DurationService from "app/services/DurationService";
import { CATEGORY_PREFIX } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import { isHasNoWWPermissions } from "app/pages/website-performance/helpers/websitePerformanceHelpers"; // Import for ctrl logic
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { type FetchParam, DefaultFetchService } from "app/services/fetchService";

import type {
    TrafficDataByDeviceType,
    IndustryDataByDeviceType,
    IIndustryData,
} from "../types";

interface UseTrafficChannelsDataProps<T> {
    category: ICategory | undefined;
    select: (mmxData: TrafficDataByDeviceType | null, rawIndustryData?: IndustryDataByDeviceType) => T;
}

interface UseTrafficChannelsDataReturn<T> {
    data: T | null;
    isLoading: boolean;
    isError: boolean;
}

const fetchIndustryData = async (params: FetchParam): Promise<IndustryDataByDeviceType | undefined> => {
    const fetchService = DefaultFetchService.getInstance();
    const industryKey = params.keys;

    try {
        const endpoint = "api/WidgetKpis/TrafficSourcesOverviewAverage/GetPieChartData";
        const totalData = await fetchService.get<{ Data: IIndustryData }>(endpoint, { ...params, webSource: "Total" });

        return {
            Total: totalData.Data[industryKey],
        };
    } catch (err) {
        return undefined;
    }
};

export const useTrafficChannelsData = <T>({
    category,
    select,
}: UseTrafficChannelsDataProps<T>): UseTrafficChannelsDataReturn<T> => {
    const fetchService = DefaultFetchService.getInstance();

    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);

    const { params, state } = useCurrentStateAndParams();
    const { country, duration, comparedDuration, isWWW, key: keys } = params;
    const configId = proNavigator.getConfigId(state);
    const { from, to, isWindow } = DurationService.getDurationData(duration, comparedDuration, configId).forAPI;
    const includeSubDomains = isWWW === "*";
    const timeGranularity = duration === "28d" ? "Daily" : "Monthly";

    useEffect(() => {
        async function fetchData() {
            setData(null);
            setIsLoading(true);
            setIsError(false);

            const isWWOApiController = isHasNoWWPermissions(country);
            const ctrl = isWWOApiController
                ? "WorldwideOverviewTotal/TrafficSourcesTotalOverview"
                : "MarketingMixTotal/TrafficSourcesOverview";
            const params = { country, from, to, includeSubDomains, isWindow, timeGranularity };

            try {
                const dataPromise = fetchService
                    .get<{ Data: TrafficDataByDeviceType }>(`widgetApi/${ctrl}/PieChart`, { ...params, keys })
                    .then((res) => res.Data);

                let industryDataPromise: Promise<IndustryDataByDeviceType> | undefined;

                if (category && category.id !== "no-category") {
                    industryDataPromise = fetchIndustryData({ ...params, keys: CATEGORY_PREFIX + category.id });
                }

                const [mmXTrafficSources, industryData] = await Promise.all([dataPromise, industryDataPromise]);
                const transformed = select(mmXTrafficSources, industryData);
                setData(transformed);
            } catch (err) {
                setIsError(true);
                Sentry.error(`Failed to fetch All Traffic Channel Overview barChart -`, err);
            } finally {
                setIsLoading(false);
            }
        }

        if (keys) {
            void fetchData();
        } else {
            setIsLoading(false);
            setData(null);
            setIsError(false);
        }
    }, [
        country,
        from,
        includeSubDomains,
        isWindow,
        keys,
        timeGranularity,
        to,
        category,
        fetchService,
        select,
    ]);

    return { data, isLoading, isError };
};
